'use client';

import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailSavedToastClicked from '@alltrails/amplitude/events/logTrailSavedToastClicked';
import logTrailSavedToastViewed from '@alltrails/amplitude/events/logTrailSavedToastViewed';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Toast from '@alltrails/denali/components/Toast';
import { favoritesListId } from '@alltrails/lists/utils/listUtils';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type WebList from '@alltrails/types/WebList';
import { useAppSelector } from '@/utils/redux';

const getViewListUrl = (savedLists: WebList[], currentUserSlug: string | undefined, languageRegionCode: LanguageRegionCode) => {
  if (savedLists.length === 1) {
    const list = savedLists[0];
    const path = list.id === favoritesListId ? `/members/${currentUserSlug}/favorites` : `/explore/list/${list.slug}`;
    return wrapUrlSafe(path, languageRegionCode);
  }

  if (!currentUserSlug) {
    return undefined;
  }

  return wrapUrlSafe(`/members/${currentUserSlug}/lists`, languageRegionCode);
};

type SaveSuccessToastProps = {
  isOpen: boolean;
  savedLists: WebList[];
  setIsOpen: (isOpen: boolean) => void;
};

export default function SaveSuccessToast({ isOpen, savedLists, setIsOpen }: SaveSuccessToastProps) {
  if (!isOpen || savedLists.length === 0) {
    return null;
  }

  return <SaveSuccessToastContent savedLists={savedLists} setIsOpen={setIsOpen} />;
}

function SaveSuccessToastContent({ savedLists, setIsOpen }: Omit<SaveSuccessToastProps, 'isOpen'>) {
  const router = useRouter();
  const languageRegionCode = useLanguageRegionCode();
  const currentUserSlug = useAppSelector(state => state.context?.currentUser?.slug);
  const firstList = savedLists[0];
  const firstListName = firstList.title;
  const viewListUrl = getViewListUrl(savedLists, currentUserSlug, languageRegionCode);

  useEffect(() => {
    logTrailSavedToastViewed({ list_id: firstList.id, list_type: firstList.id === favoritesListId ? 'favorites' : 'custom list' });
  }, [firstList.id]);

  const message =
    savedLists.length > 1 ? (
      <FormattedMessage
        defaultMessage="Saved to {listName} and {otherCount, plural, one {# other list} other {# other lists}}"
        values={{ listName: firstListName, otherCount: savedLists.length - 1 }}
      />
    ) : (
      <FormattedMessage defaultMessage="Saved to {listName}" values={{ listName: firstListName }} />
    );

  return (
    <ModalPortal>
      <Toast
        action={
          viewListUrl
            ? () => {
                logTrailSavedToastClicked({ list_id: firstList.id, list_type: firstList.id === favoritesListId ? 'favorites' : 'custom list' });
                router.push(viewListUrl);
              }
            : undefined
        }
        actionText={<FormattedMessage defaultMessage="View {count, plural, one {list} other {lists}}" values={{ count: savedLists.length }} />}
        isOpen
        message={message}
        position="alternative"
        setIsOpen={setIsOpen}
        type="neutral"
      />
    </ModalPortal>
  );
}
